import { useMemo } from 'react';
import { Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useTheme } from 'components/core/theme-provider';
import { useGetTools } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { ITool } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import type { AgentAttachedTool } from 'features/chat/agent-chat-module/types/agent.service.type';
import { cn } from 'lib/utils';
import { getAskAgentOwnerToAddMessage } from './ask-agent-owner-message';
import { AgentConfigToolCard } from './agent-config-tool-card';

type ToolsSectionProps = {
  enabled: boolean;
  canEdit?: boolean;
  selectedToolIds: string[];
  attachedTools?: AgentAttachedTool[];
  onRemoveTool: (toolId: string) => void;
};

export function ToolsSection({
  enabled,
  canEdit = true,
  selectedToolIds,
  attachedTools = [],
  onRemoveTool,
}: ToolsSectionProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const emptyText = theme === 'dark' ? 'text-muted-foreground' : 'text-grey-500';
  const attachedById = useMemo(() => {
    const map = new Map<string, AgentAttachedTool>();
    for (const tool of attachedTools) {
      map.set(tool.id, tool);
    }
    return map;
  }, [attachedTools]);
  const { data: generalToolsData } = useGetTools({
    page: 1,
    page_size: 100,
  });
  const { data: apiToolsData } = useGetTools({
    tool_type: 'api',
    page: 1,
    page_size: 50,
  });
  const { data: mcpToolsData } = useGetTools({
    tool_type: 'mcp_server',
    page: 1,
    page_size: 50,
  });
  const { data: composioToolsData } = useGetTools({
    tool_type: 'composio',
    page: 1,
    page_size: 50,
  });
  const { data: openConnectorToolsData } = useGetTools({
    tool_type: 'open_connector',
    page: 1,
    page_size: 50,
  });
  const allTools = useMemo<ITool[]>(
    () => [
      ...(generalToolsData?.tools || []),
      ...(apiToolsData?.tools || []),
      ...(mcpToolsData?.tools || []),
      ...(composioToolsData?.tools || []),
      ...(openConnectorToolsData?.tools || []),
    ],
    [
      generalToolsData?.tools,
      apiToolsData?.tools,
      mcpToolsData?.tools,
      composioToolsData?.tools,
      openConnectorToolsData?.tools,
    ]
  );

  const toolsById = useMemo(() => {
    const map = new Map<string, ITool>();
    for (const tool of allTools) {
      map.set(tool.id, tool);
    }
    return map;
  }, [allTools]);

  const selectedTools = useMemo(
    () =>
      selectedToolIds.map((id) => {
        const catalogTool = toolsById.get(id);
        if (catalogTool) return catalogTool;
        const attached = attachedById.get(id);
        if (attached) {
          return {
            id: attached.id,
            name: attached.name,
            type: attached.type as ITool['type'],
            providers: attached.providers,
          };
        }
        return { id, name: id };
      }),
    [attachedById, selectedToolIds, toolsById]
  );

  return (
    <div className="space-y-4">
      {selectedTools.length > 0 ? (
        <div className="grid gap-4">
          {selectedTools.map((tool) => (
            <AgentConfigToolCard
              key={tool.id}
              tool={tool}
              onRemove={onRemoveTool}
              disabled={!enabled}
            />
          ))}
        </div>
      ) : (
        <div
          className={cn(
            'flex h-36 flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-grey-100 bg-grey-50 text-center dark:border-border dark:bg-muted/30',
            !enabled && 'opacity-50'
          )}
        >
          <Wrench className={cn('size-8', emptyText)} strokeWidth={1.5} />
          <p className={cn('max-w-sm text-xs font-medium leading-body', emptyText)}>
            {canEdit
              ? t('NO_TOOLS_ADDED_CLICK_ADD_TO_GET_STARTED') ||
              "No tools added yet. Click 'Add' to get started."
              : getAskAgentOwnerToAddMessage(t, t('TOOLS') || 'tools')}
          </p>
        </div>
      )}
    </div>
  );
}
